import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/je5va6ct_FRAUD%20IMAGE.jpg';

const FraudPrevention = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="fraud-prevention-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="fixed inset-0 top-20 bg-white/0" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="fraud-back-button"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          {/* Intro */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8" data-testid="fraud-page-title">
              Fraud & Risk Prevention
            </h1>

            <div className="space-y-5 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                At eCOMM Merchant Solutions we recognise the importance of employing techniques to guard against fraud. Fraudsters have become increasingly sophisticated in obtaining personal data to commit acts of fraud and obtain customer information. These attacks undermine merchant businesses and directly impact both the customer and the retailer.
              </p>
              <p>
                Implementing fraud strategies and managing chargebacks effectively within your organisation will ultimately safeguard you and your customers and limit your exposure to losses as a result of fraud.
              </p>
              <p>
                To assist you in the fight against fraud we have provided some tips you can adopt to manage risk and chargebacks within your business.
              </p>
            </div>
          </div>

          {/* Merchant Tips */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-testid="fraud-merchant-tips-title">
              Merchant Tips to Fight Online Fraud
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
              eCOMM Merchant Solutions recommends a number of strategies that merchants can use to help limit exposure to Fraud and to develop an all-encompassing protection approach. The key to managing risk and fighting fraudsters is to build prevention, detection and monitoring controls into your business.
            </p>

            {/* Prevention */}
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Prevention</h3>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              Only the individual merchant can determine which procedures to implement in order to maintain a correct balance between fighting fraud and accepting credit cards from legitimate customers.
            </p>
            <ul className="space-y-3 text-gray-700 text-base leading-relaxed mb-10 list-disc pl-6">
              <li>Consider not accepting credit cards from the customer unless full information is provided including the complete address and phone numbers</li>
              <li>Know Your Customer (KYC). By calling a customer with the information provided you can further confirm the security of sales. If the person contacted at the phone number provided doesn't recognize the name of the customer, the order is likely fraudulent</li>
              <li>If they are available, utilise CVC2 (Mastercard) and CVV2 (Visa). These two numbers are the three unique digits on the back of a Mastercard or Visa credit/debit card. These are used in situations where the card is not present (CNP Transactions), e.g., mail order, telephone order and online card transactions</li>
              <li>If a transaction is not made face to face, it is a good idea to send a confirmation directly to the cardholder's address to help identify the transaction when it appears on a billing statement</li>
              <li>Process refunds to your customers' accounts quickly, always using the same card number from the original sale</li>
              <li>Stop recurring transactions that a cardholder cancelled; this will reduce the number of repetitive chargebacks by the same customer</li>
              <li>Always adhere to your returns policy and make sure your customer has a clear understanding of the returns procedure. This helps with reducing chargebacks but also saves valuable time and resources within customer services</li>
            </ul>

            {/* Detection */}
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Detection</h3>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              Understanding your customer models and behaviours can help in alerting you to suspected fraudulent activity, for example:
            </p>
            <ul className="space-y-3 text-gray-700 text-base leading-relaxed mb-10 list-disc pl-6">
              <li><strong>Late night orders</strong> – Fraud increases late at night</li>
              <li><strong>Ordering in high volume</strong> – Cyber crooks don't care how high the bill is, since they don't intend to pay it</li>
              <li><strong>Physical address</strong> – Physical address must match the credit card billing address</li>
              <li><strong>Shipping address</strong> – This must be in the same area as the record of the customer's address. This is an indicator and cannot be considered as suspicious in all cases where gifts are purchased</li>
              <li><strong>E-mail address</strong> – E-mail addresses which have no apparent connection to the customer's name or include random characters could be potential attempts to mask identity but again, this is an indicator</li>
              <li><strong>Repeated attempts</strong> – Repeated attempts on the same card number must be treated as suspicious</li>
            </ul>

            {/* Monitoring */}
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Monitoring</h3>
            <ul className="space-y-3 text-gray-700 text-base leading-relaxed list-disc pl-6">
              <li>Control employee access to financial systems</li>
              <li>Analyse customer behaviour and activity so as to understand your business risk indicators</li>
              <li>Work closely with your business partners in understanding how you can leverage fraud control to best suit your business model</li>
              <li>Have the relevant Security and Fraud policies required to maintain a risk based framework within your company</li>
            </ul>
          </div>

          {/* Merchant Tips Diagram */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/eff7jqe3_MERCHANT%20TIPS.png"
              alt="Merchant Tips for Preventing Chargebacks - The Process"
              className="w-full h-auto"
              data-testid="fraud-diagram"
            />
          </div>

          {/* Why Chargebacks */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-testid="fraud-chargebacks-title">
              Why You May Have Received a Chargeback
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4">
              Understanding the reasons behind claims from your customers can help identify the main cause and provide you with valuable information on where you need to make changes or consider adopting a new approach within your business. This helps to reduce chargebacks but also saves valuable time and customer service resource.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4 font-semibold">
              Possible reasons for chargebacks include:
            </p>
            <ul className="space-y-3 text-gray-700 text-base leading-relaxed list-disc pl-6">
              <li>Customer did not receive a product or service</li>
              <li>Customer does not recognise the charge or business name on their card statement</li>
              <li>Customer believes the product or service was defective, damaged or not as it was described</li>
              <li>Customer was a victim of fraud; card was stolen or used without consent</li>
              <li>Customer refund was not processed in a timely manner</li>
            </ul>
          </div>

          {/* Avoiding Chargebacks */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-testid="fraud-avoiding-title">
              Avoiding and Managing Chargebacks
            </h2>
            <div className="space-y-5 text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
              <p>
                The best approach to prevent chargebacks is to create best practices that are followed consistently by all employees. For example, always process a refund back to the original card number in a timely manner.
              </p>
              <p>
                Chargebacks can cost you time and money – but it's important to remember that many chargebacks can be prevented and quite often, it begins with comprehensive employee training.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-base font-bold">
                <span className="text-red-600">To contact our Fraud and Chargebacks team please email:</span>{' '}
                <a
                  href="mailto:fraudops@ecomm365.com"
                  className="text-[#1F4E79] underline hover:text-[#163a5f] transition-colors duration-200"
                  data-testid="fraud-email-link"
                >
                  fraudops@ecomm365.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default FraudPrevention;
